// @flow
import Cleave from 'cleave.js/react';
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import { map } from 'rxjs/operators';
import update from 'immutability-helper';
import inputStyle from '../../../../assets/styles/input.scss';
import Modal from '../../../../elements/Modal/Modal';
import { FormValidator } from '../../../../utils/form';
import NewFlightAutocompleteInput from './NewFlightAutocompleteInput/NewFlightAutcompleteInput';
import NewFlightDate from './NewFlightDate/NewFlightDate';
import moment from 'moment/moment';
import { getLocalizedCountry } from '../../../local-values/utils/countries';
import VideoModal from '../../../home/components/Discover/VideoModal';

import style from './NewFlightForm.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';
import discoverStyle from '../../../../modules/home/components/Discover/Discover.scss';

import type { Observable } from 'rxjs';
import type { FormValidationResult } from '../../../../utils/form';
import type { SearchLocationRequest } from '../../api';
import type { Flight, User } from '../../../../entities';
import type { SearchResult } from './NewFlightAutocompleteInput/NewFlightAutcompleteInput';
import type { LocalValuesState } from '../../../local-values/duck';

type DepartureFlightValue = {
  departureAdmin1?: number;
  departureAdmin2?: number;
  departureCountry?: string;
  label?: string;
};

type ArrivalFlightValue = {
  arrivalAdmin1?: number;
  arrivalAdmin2?: number;
  arrivalCountry?: string;
  label?: string;
};

type DateValue = {
  flightDate?: number;
  object?: any;
};

type Props = {
  +locale: string;
  +localValues: LocalValuesState;
  +postFlight: (flightDate: string, departureCountry: string, departureAdmin1: number,
                 departureAdmin2: number, arrivalCountry: string, arrivalAdmin1: number,
                 arrivalAdmin2: number) => void;
  +editFlight?: (flightDate: string, departureCountry: string, departureAdmin1: number,
                 departureAdmin2: number, arrivalCountry: string, arrivalAdmin1: number,
                 arrivalAdmin2: number, id: string) => void;
  +getFlight?: (id: string) => void;
  +isPosting?: boolean;
  +searchLocation: (request: SearchLocationRequest) => Observable<any>;
  +isFetching?: boolean;
  +resetState?: () => void;
  +id?: string;
  +flight?: Flight;
  +handleOpenAuthModal?: (openLoginView?: boolean) => void;
  +authenticated?: boolean;
  +user?: User;
  +mode: 'edit' | 'create';
  +authModalVisible?: boolean;
  +abTest?: boolean;
  +postPhone: (phone: string, country: string, enableSmsNotifications: boolean) => void;
};

type State = {
  departure: {
    value: DepartureFlightValue;
    errors?: FormValidationResult[];
  };
  departureItems: SearchResult[];
  arrival: {
    value: ArrivalFlightValue;
    errors?: FormValidationResult[];
  };
  arrivalItems: SearchResult[];
  date: {
    value: DateValue;
    errors?: string[];
  };
  searchIsLoading: boolean;
  modalVisible: boolean;
  phoneModalVisible: boolean;
  phone: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  termsModalVisible: boolean;
};

const debounceTime = 500;

const videoUrl = 'https://www.youtube.com/embed/Cnuj0VmVg9o?rel=0&amp;controls=0&amp;showinfo=0&amp;autoplay=1';

export default class NewFlightForm extends Component<Props, State> {
  dataInitialized = false;
  formValidator: FormValidator;
  formValidatorPhone: FormValidator;
  timeoutHandle: TimeoutID;
  // if next button is pressed and authentication is missing
  nextButtonPressHold: boolean = false;

  constructor(props: Props) {
    super();
    this.state = {
      departure: {
        value: {},
      },
      arrival: {
        value: {},
      },
      departureItems: [],
      arrivalItems: [],
      date: {
        value: {},
      },
      searchIsLoading: false,
      modalVisible: false,
      phoneModalVisible: false,
      phone: { value: props.user ? props.user.phone : '', pristine: true },
      termsModalVisible: false,
    };
    this.formValidator = new FormValidator({
      departure: {
        required: (departure: DepartureFlightValue) => !departure || (departure && !departure.departureCountry),
      },
      arrival: {
        required: (arrival: ArrivalFlightValue) => !arrival || (arrival && !arrival.arrivalCountry),
      },
      date: {
        required: 'required',
      },
    });
    this.formValidatorPhone = new FormValidator({
      phone: {
        required: 'required',
      },
    });
  }

  componentWillUnmount() {
    const { resetState } = this.props;
    if (resetState) resetState();
  }

  componentDidMount() {
    const { getFlight, flight, id } = this.props;
    if (!flight && getFlight && id) {
      getFlight(id);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { authModalVisible, user, authenticated, flight } = this.props;
    // This means it was just authenticated and came from a "Next" button click
    if (this.nextButtonPressHold && user && authenticated) {
      this.handleOpenPhoneModal();
    }
    // This means: when the modal closes
    if (authModalVisible === false && prevProps.authModalVisible === true) {
      this.nextButtonPressHold = false;
    }
    // This means he just authenticated
    if (!prevProps.authenticated && authenticated && user) {
      this.setState({ phone: { value: user.phone, pristine: true } });
    }
    // This means if flight exists and haven't been initialized
    if (flight && !this.dataInitialized) {
      this.dataInitialized = true;
      const [departureLabel, arrivalLabel] = this.formatFlightLocations(flight);
      this.setState({
        departure: {
          value: {
            departureAdmin1: flight.departureAdmin1 && flight.departureAdmin1.id,
            departureAdmin2: flight.departureAdmin2 && flight.departureAdmin2.id,
            departureCountry: flight.departureCountry.code,
            label: departureLabel,
          },
        },
        arrival: {
          value: {
            arrivalAdmin1: flight.arrivalAdmin1 && flight.arrivalAdmin1.id,
            arrivalAdmin2: flight.arrivalAdmin2 && flight.arrivalAdmin2.id,
            arrivalCountry: flight.arrivalCountry.code,
            label: arrivalLabel,
          },
        },
        date: {
          value: {
            flightDate: flight.flightDate,
            object: moment(flight.flightDate).toDate(),
          },
        },
      });
    }
  }

  formatFlightLocations(flight: Flight): [string, string] {
    const { localValues } = this.props;
    let d, a;
    if (flight.departureAdmin2) {
      d = `${flight.departureAdmin2.name}, ${flight.departureAdmin1.name}, 
      ${getLocalizedCountry(flight.departureCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    } else if (flight.departureAdmin1) {
      d = `${flight.departureAdmin1.name}, 
      ${getLocalizedCountry(flight.departureCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    } else {
      d = `${getLocalizedCountry(flight.departureCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    }
    if (flight.arrivalAdmin2) {
      a = `${flight.arrivalAdmin2.name}, ${flight.arrivalAdmin1.name}, 
      ${getLocalizedCountry(flight.arrivalCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    } else if (flight.arrivalAdmin1) {
      a = `${flight.arrivalAdmin1.name}, 
      ${getLocalizedCountry(flight.arrivalCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    } else {
      a = `${getLocalizedCountry(flight.arrivalCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`
    }
    return [d, a];
  }

  handleChangeTextDeparture(e: Event, value: string) {
    const { searchLocation } = this.props;
    if (this.timeoutHandle && window) window.clearTimeout(this.timeoutHandle);
    this.setState({
      departure: {
        value: {
          // ...this.state.departure.value,
          label: value,
        },
      },
      departureItems: [],
      searchIsLoading: true,
    });
    this.timeoutHandle = setTimeout(() => {
        const req: { [key: string]: any } = {
          source: 1,
          inversenull: 1,
          types: 'ADMIN1,ADMIN2',
        };
        if (value) {
          req.q = value;
        }
        searchLocation(req)
          .pipe(
            map((resp: SearchResult[]) => resp.splice(0, 5))
          )
          .subscribe((departureItems: SearchResult[]) =>
              this.setState({ departureItems, searchIsLoading: false })
            , e => this.setState({ searchIsLoading: false }));
      }
      , debounceTime);
  }

  handleChangeTextArrival(e: Event, value: string) {
    const { searchLocation } = this.props;
    if (this.timeoutHandle && window) window.clearTimeout(this.timeoutHandle);
    this.setState({
      arrival: {
        value: {
          // ...this.state.arrival.value,
          label: value,
        },
      },
      arrivalItems: [],
      searchIsLoading: true,
    });
    this.timeoutHandle = setTimeout(() => {
      if (value) {
        return searchLocation({
          dest: 1,
          inversenull: 1,
          q: value,
          types: 'ADMIN1,ADMIN2',
        })
          .pipe(
            map((resp: SearchResult[]) => resp.splice(0, 5))
          )
          .subscribe((arrivalItems: SearchResult[]) =>
              this.setState({ arrivalItems, searchIsLoading: false })
            , e => this.setState({ searchIsLoading: false }));
      }
    }, debounceTime);
  }

  handleSelectDeparture(label: string, item: SearchResult) {
    return this.setState({
      departure: {
        value: {
          departureAdmin1: item.admin1 ? item.admin1.id : undefined,
          departureAdmin2: item.admin2 ? item.admin2.id : undefined,
          departureCountry: item.country.code,
          label,
        },
      },
    });
  }

  handleSelectArrival(label: string, item: SearchResult) {
    return this.setState({
      arrival: {
        value: {
          arrivalAdmin1: item.admin1 ? item.admin1.id : undefined,
          arrivalAdmin2: item.admin2 ? item.admin2.id : undefined,
          arrivalCountry: item.country.code,
          label,
        },
      },
    });
  }

  handleSelectDate(date: Date) {
    this.setState((state: State) => ({
      date: update(state.date, {
        $set: {
          value: {
            flightDate: date.getTime(),
            object: date,
          },
        },
      }),
    }));
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name } = e.target;
      this.setState({ [name]: { value: this.state[name].value } });
    }
  }
  
  handleOpenPhoneModal = () => {
    const { departure, arrival, date } = this.state;
    const { user, authenticated, handleOpenAuthModal } = this.props;

    const errors = this.formValidator.validateForm({
      departure: departure.value,
      arrival: arrival.value,
      date: date.value.flightDate,
    });

    if (errors.length > 0) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
      return;
    }

    if (!user || !authenticated) {
      if (handleOpenAuthModal instanceof Function) {
        this.nextButtonPressHold = true;
        handleOpenAuthModal();
        return;
      }
    }
    
    this.setState({ phoneModalVisible: true });
  }

  handleClosePhoneModal = () => {
    this.setState({ phoneModalVisible: false });
  };

  handleChangePhone = (e: Event) => {
    const { user } = this.props;
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      const pristine = user ? user.phone === rawValue : false;
      this.setState({ phone: { value, pristine } });
    }
  };
  
  handleClickOpenTermsModal = () => {
    const { phone } = this.state;

    const errors = this.formValidatorPhone.validateForm({
      phone: phone.value,
    });

    if (errors.length > 0) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
      return;
    }
    
    this.setState({ termsModalVisible: true, phoneModalVisible: false });
  };
  
  handleCloseTermsModal = () => {
    this.setState({ termsModalVisible: false });
  };

  handleClick = () => {
    const { departure, arrival, date, phone } = this.state;
    const { postFlight, editFlight, id, postPhone, user } = this.props;

    if (!id) {
      postPhone(phone.value, user && user.phoneCountry ? user.phoneCountry.code : 'PE', false);
      postFlight(moment(date.value.flightDate).utc().format('YYYY-MM-DD'),
        departure.value.departureCountry || '', departure.value.departureAdmin1 || 0,
        departure.value.departureAdmin2 || 0, arrival.value.arrivalCountry || '', arrival.value.arrivalAdmin1 || 0,
        arrival.value.arrivalAdmin2 || 0);
    } else {
      if (editFlight) {
        editFlight(moment(date.value.flightDate).utc().format('YYYY-MM-DD'),
          departure.value.departureCountry || '', departure.value.departureAdmin1 || 0,
          departure.value.departureAdmin2 || 0, arrival.value.arrivalCountry || '', arrival.value.arrivalAdmin1 || 0,
          arrival.value.arrivalAdmin2 || 0, id);
      }
    }
    
    this.setState({
      termsModalVisible: false,
    });
  }

  handleOpenModal() {
    this.setState({ modalVisible: true });
  }

  handleCloseModal() {
    this.setState({ modalVisible: false });
  }

  render() {
    const { modalVisible, phoneModalVisible, termsModalVisible } = this.state;
    const {
      departure, departureItems, arrival, arrivalItems, searchIsLoading, date, phone,
    } = this.state;
    const { locale, isPosting, isFetching, mode, localValues, abTest, user } = this.props;
    const departurePlaceholder = departure.errors && departure.errors[0] === 'required' ?
      'newFlight.departure.error.required' : 'newFlight.departure.placeholder';
    const arrivalPlaceholder = arrival.errors && arrival.errors[0] === 'required' ?
      'newFlight.arrival.error.required' : 'newFlight.arrival.placeholder';
    const datePlaceholder = date.errors && date.errors[0] === 'required' ?
      'newFlight.date.error.required' : 'newFlight.date.placeholder';
    const disabled = isPosting || isFetching;

    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'preferences.security.phone.phone.error.required',
    });
    
    let countryPhonePrefix = localValues.countriesMap ? localValues.countriesMap['PE'].phonePrefix : '';
    if (user && user.phoneCountry && localValues.countriesMap) {
      countryPhonePrefix = localValues.countriesMap[user.phoneCountry.code].phonePrefix;
    }

    return (
      <Fragment>
        <section className={classNames(style.formWrap, { [style.b]: abTest })}>
          {mode === 'create' ?
            <FormattedMessage id="newFlight.form.title">
              {txt => <h2>{txt}</h2>}
            </FormattedMessage> :
            <FormattedMessage id="editFlight.title">
              {txt => <h2>{txt}</h2>}
            </FormattedMessage>}
          <form className="" autoComplete="off">
            <article className={classNames(style.block)}>
              <NewFlightAutocompleteInput localValues={localValues}
                                          translateId={departurePlaceholder}
                                          loadingTranslateId="newFlight.departure.loading"
                                          noResultsTranslateId="newFlight.departure.noResults"
                                          name="departure"
                                          icon={icons.iconLocationSquareMedium}
                                          searchIsLoading={searchIsLoading}
                                          items={departureItems}
                                          data={(departure: any)}
                                          isPosting={disabled}
                                          mode={mode}
                                          handleChange={this.handleChangeTextDeparture.bind(this)}
                                          handleSelect={this.handleSelectDeparture.bind(this)}/>
            </article>
            <article className={classNames(style.block)}>
              <NewFlightAutocompleteInput localValues={localValues}
                                          translateId={arrivalPlaceholder}
                                          loadingTranslateId="newFlight.arrival.loading"
                                          noResultsTranslateId="newFlight.arrival.noResults"
                                          name="arrival"
                                          icon={icons.iconAirplaneMedium}
                                          searchIsLoading={searchIsLoading}
                                          items={arrivalItems}
                                          data={(arrival: any)}
                                          isPosting={disabled}
                                          mode={mode}
                                          handleChange={this.handleChangeTextArrival.bind(this)}
                                          handleSelect={this.handleSelectArrival.bind(this)}/>
            </article>
            <article className={classNames(style.block, style.block)}>
              <NewFlightDate handleSelectDate={this.handleSelectDate.bind(this)}
                             handleFocus={this.handleFocus.bind(this)}
                             translateId={datePlaceholder}
                             data={date}
                             isPosting={disabled}
                             locale={locale}/>
            </article>
            {mode === 'create' ?
              <FormattedMessage id="newFlight.button">
                {txt => <button type="button"
                                disabled={disabled || isPosting}
                                onClick={this.handleOpenPhoneModal}
                                className={classNames(buttonStyle.btn, buttonStyle.orange, style.submit,
                                  { [style.disabled]: disabled })}>{txt}</button>}
              </FormattedMessage> :
              <FormattedMessage id="editFlight.button">
                {txt => <button type="button"
                                disabled={disabled || (isFetching || isPosting)}
                                onClick={this.handleClick}
                                className={classNames(buttonStyle.btn, buttonStyle.orange, style.submit,
                                  { [style.disabled]: disabled })}>{txt}</button>}
              </FormattedMessage>}
            {mode === 'create' &&
            <button className={classNames(discoverStyle.discover, discoverStyle.orange, style.discover)}
                    onClick={this.handleOpenModal.bind(this)}
                    type="button">
              <div className={discoverStyle.circleAndPlay}>
                <i className={icons.symbolPlay}/>
              </div>
              <FormattedMessage id={abTest ? "newFlight.watchTutorial" : "newFlight.learnReserve"}>{
                txt => <p>{txt}</p>
              }</FormattedMessage>
            </button>}
          </form>
          {mode === 'create' && <VideoModal handleClose={this.handleCloseModal.bind(this)}
                                            modalVisible={modalVisible}
                                            url={videoUrl}/>}
        </section>
        <Modal open={termsModalVisible}
               handleClose={this.handleCloseTermsModal}
               width={700}>
          <h3 className={style.phoneModalTitle}>Reglamento para Viajeros</h3>
          <ul className={style.phoneModalList}>
            <li>Antes de comprar un producto, el viajero debe verificar que la fecha de entrega de la tienda esté dentro de su fecha de viaje.</li>
            <li>El viajero debe verificar si el precio de la cotización es igual al precio de la tienda. En caso no concuerde, debe reportarlo con Qempo y no reservar el pedido.</li>
            <li>Es obligatorio que el viajero compre los productos del mismo enlace que el cliente coloca en la plataforma; caso contrario, se cancelará el pedido.</li>
            <li>El viajero debe realizar la compra del producto en el momento en que lo reserva.</li>
            <li>El viajero debe subir el comprobante de compra en la plataforma. De verificarse que el comprobante no corresponde a la compra, la orden se cancelará automáticamente sin lugar a reclamo.</li>
            <li>Es obligatorio que el viajero responda los mensajes a los compradores. De no responder en un periodo de 48 horas, Qempo tiene la potestad de cancelar el pedido para que otro viajero lo tome.</li>
            <li>Está totalmente prohibido compartir datos personales, como números telefónicos y correo, mediante el sistema de mensajería de Qempo.</li>
            <li>Está totalmente prohibido ofrecer el servicio de importación de productos a nuestros clientes fuera de la plataforma de Qempo. De no cumplir con este reglamento, Qempo tiene la potestad de banear y penalizar a los viajeros.</li>
            <li>Qempo solo realizará el pago acordado en la plataforma. Si surge algún pago adicional durante el traslado del producto, deberá ser asumido por el viajero. Cualquier duda que surja sobre impuestos de aduanas, taxes o shipping, deben comunicarse con Qempo al WhatsApp +51 17064549 antes de tomar el pedido.</li>
            <li>El viajero tiene 2 días hábiles desde la fecha de viaje registrada en la plataforma para realizar la entrega de los productos en Lima. De no recibir ningún aviso, Qempo puede cancelar todos los pedidos tomados por el viajero.</li>
            <li>De no tener un pedido completo, se debe informar con anticipación al comprador y aceptar la solución que este brinde. Si el comprador decide no esperar, tendrá la potestad de cancelar el pedido completo.</li>
            <li>Si el viajero entrega un producto golpeado, roto o el empaque en mal estado, Qempo tiene la potestad de no aceptarlo y cancelar el pedido.</li>
          </ul>
          <FormattedMessage id="newFlight.button">
            {txt => <button type="button"
                            disabled={disabled}
                            onClick={this.handleClick}
                            className={classNames(buttonStyle.btn, buttonStyle.orange, style.phoneModalSubmit,
                              { [style.disabled]: disabled })}>{txt}</button>}
          </FormattedMessage>
        </Modal>
        <Modal open={phoneModalVisible}
               handleClose={this.handleClosePhoneModal}
               width={544}>
          <h3 className={style.phoneModalTitle}>Información de contacto</h3>
          <p className={style.phoneModalText}>
            Crearemos una página especial para tu viaje con tu contacto de WhatsApp para que puedas compartirlo con tus clientes, amigos y redes sociales.
          </p>
          <div className={style.modalFormWrap}>
            <label className={style.inputLabel}>Whatsapp</label>
            <label className={classNames(style.prefix, { [style.disabled]: isPosting, [style.error]: phone.errors && phone.errors.length })}>
              {countryPhonePrefix}
            </label>
            <Cleave value={phone.value || ''}
                    options={{
                      phone: true,
                      phoneRegionCode: 'PE',
                      delimiter: '-',
                    }}
                    className={classNames(inputStyle.input, { [inputStyle.red]: phone.errors && phone.errors.length },
                      { [inputStyle.lightBlue]: !!phone.value }, style.input)}
                    onChange={this.handleChangePhone}
                    disabled={disabled}
                    name="phone"/>
            {phoneError}
          </div>
          <FormattedMessage id="newFlight.button">
            {txt => <button type="button"
                            disabled={disabled}
                            onClick={this.handleClickOpenTermsModal}
                            className={classNames(buttonStyle.btn, buttonStyle.orange, style.phoneModalSubmit,
                              { [style.disabled]: disabled })}>{txt}</button>}
          </FormattedMessage>
        </Modal>
      </Fragment>
    );
  }
}

NewFlightForm.propTypes = {
  locale: PropTypes.string.isRequired,
  localValues: PropTypes.object,
  postFlight: PropTypes.func,
  editFlight: PropTypes.func,
  getFlight: PropTypes.func,
  isPosting: PropTypes.bool,
  searchLocation: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  resetState: PropTypes.func,
  id: PropTypes.string,
  flight: PropTypes.object,
  handleOpenAuthModal: PropTypes.func,
  authenticated: PropTypes.bool,
  user: PropTypes.object,
  mode: PropTypes.string,
  authModalVisible: PropTypes.bool,
  abTest: PropTypes.bool,
  postPhone: PropTypes.func.isRequired,
};

NewFlightForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-flight/components/NewFlightForm/NewFlightForm.jsx